/**
 * Library Object Code Preview using Export Templates
 * Generates code preview for library objects (glossary items) using the custom export template system
 */

import { ExportTemplate, ExportTemplatesConfig, TemplateRenderContext } from '@/types/exportTemplates';
import { getPresetTemplates } from './exportTemplatePresets';
import { renderExportTemplate } from './templateEngine';
import { Game } from '@/lib/stores/storeTypes';
import { getDefaultPlatformForGame } from '@/lib/utils/platformMapping';
import { ObjectItem } from "@/lib/stores/objectsStore";
import { getCachedTemplates } from "@/lib/stores/templatesStore";
import { sanitizeFilename } from './libraryProcessorShared';
import { processLibraryItem } from './libraryProcessorShared';

/**
 * Get the active export template for a game
 */
function getActiveTemplate(game: Game | null, platform?: string): ExportTemplate | null {
  if (!game) return null;
  
  const exportConfig = (game.settings as any)?.exportTemplates as ExportTemplatesConfig | undefined;
  
  // If specific platform requested, prioritize based on active template
  if (platform) {
    // For Roblox, prioritize Database templates over code templates
    if (platform === 'roblox' && exportConfig?.templates) {
      // First, check for database templates (these take priority for Roblox)
      const databaseTemplate = exportConfig.templates.find(t => 
        (t.id === 'preset-roblox-database' || t.id === 'preset-roblox-static-typed') && 
        t.platform === 'roblox'
      );
      if (databaseTemplate) {
        console.log('🎯 Using Roblox Database template for library export:', databaseTemplate.name);
        return databaseTemplate;
      }
    }
    
    // First, check if the active template matches the requested platform
    if (exportConfig?.activeTemplateId && exportConfig.templates) {
      const activeTemplate = exportConfig.templates.find(t => t.id === exportConfig.activeTemplateId);
      if (activeTemplate && activeTemplate.platform === platform) {
        return activeTemplate;
      }
    }
    
    // If active template doesn't match, find any template for that platform
    if (exportConfig?.templates) {
      const template = exportConfig.templates.find(t => t.platform === platform);
      if (template) return template;
    }
    
    // Fallback to preset for that platform
    const presets = getPresetTemplates();
    const preset = presets.find(p => p.platform === platform);
    if (preset) return preset;
  }
  
  // Use active template (when no specific platform requested)
  if (exportConfig?.activeTemplateId && exportConfig.templates) {
    const template = exportConfig.templates.find(t => t.id === exportConfig.activeTemplateId);
    if (template) return template;
  }
  
  // Fallback to default platform preset
  const defaultPlatform = getDefaultPlatformForGame(game);
  const presets = getPresetTemplates();
  return presets.find(p => p.platform === defaultPlatform) || presets[0];
}

/**
 * Map blueprint field to template field with actual value
 */
function mapLibraryField(field: any, template: ExportTemplate): any {
  const fieldType = field.type || 'text';
  const mapping = template.fieldMappings[fieldType] || template.fieldMappings['text'];
  
  return {
    id: field.id,
    name: field.name,
    label: field.name,
    type: fieldType,
    mappedType: mapping.typeName,
    value: field.value,
    defaultValue: mapping.defaultValue,
    isRequired: field.isRequired || false,
    isTranslatable: field.isTranslatable || false,
    isMultiple: field.isMultiple || false,
    comment: field.comment || ''
  };
}

/**
 * Generate library object code preview with game context
 */
export function generateLibraryCodePreviewWithGame(
  objectItem: ObjectItem,
  game: Game | null,
  platform?: 'roblox' | 'unity' | 'godot' | 'cpp' | 'typescript'
): string | null {
  if (!objectItem || !game) return null;
  
  // Get active template
  const template = getActiveTemplate(game, platform);
  if (!template) return null;
  
  console.log('📝 Generating library code preview:', {
    objectName: objectItem.name,
    blueprintId: objectItem.blueprint_id,
    templateName: template.name,
    templateFunctions: template.functions?.length || 0,
    functionDetails: template.functions?.map(f => ({
      name: f.name,
      appliesTo: f.appliesTo,
      hasVariables: !!f.variables?.length,
      hasCommonVariables: !!f.commonVariables?.length
    }))
  });
  
  // Process the library item to extract field values
  const processedItem = processLibraryItem(objectItem, game.id);
  
  // Get blueprint for this item
  const blueprint = getCachedTemplates(game.id).find(bp => bp.id === objectItem.blueprint_id);
  
  if (!blueprint) {
    return '// No blueprint found for this library object';
  }
  
  // Map fields to template format with actual values
  const fields = processedItem.fields.map(field => mapLibraryField(field, template));
  
  // Create render context
  const context: TemplateRenderContext = {
    templateName: blueprint.name,
    templateDescription: blueprint.description,
    itemName: objectItem.name,
    itemId: objectItem.id,
    codeId: objectItem.code_id,
    fields: fields,
    metadata: {
      itemId: objectItem.id,
      codeId: objectItem.code_id,
      blueprintId: objectItem.blueprint_id,
      blueprintVersion: processedItem.blueprintVersion,
      tags: objectItem.tags || [],
      identifiers: objectItem.identifiers || []
    }
  };
  
  // Render template
  const result = renderExportTemplate(context, template);
  
  // Return instance file content (the actual library object with values)
  return result.instanceFile?.content || null;
}

/**
 * Download library object code file
 */
export function downloadLibraryCode(
  objectItem: ObjectItem,
  game: Game | null,
  platform: 'roblox' | 'unity' | 'godot' | 'cpp' | 'typescript',
  gameName?: string
): void {
  const code = generateLibraryCodePreviewWithGame(objectItem, game, platform);
  
  if (!code) {
    console.error('Failed to generate code for download');
    return;
  }
  
  // Get template for file extension
  const template = getActiveTemplate(game, platform);
  const extension = template?.instanceFile.filename.split('.').pop() || 'txt';
  
  // Generate filename
  const name = objectItem.name || 'library_object';
  const filename = `${sanitizeFilename(gameName || 'game')}-${sanitizeFilename(name)}.${extension}`;
  
  // Create and download file
  const blob = new Blob([code], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

